// ---------------- Component for the model prediction panel ------------------------
// ----------------------------------------------------------------------------------

// import all the components and hooks we need
import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";

// import the custom hooks to call the api
import { useHoldings } from "../hooks/useHoldings";
import { useAvailableModels } from "../hooks/useAvailableModels";
import { useBacktest } from "../hooks/useBacktest";
import { useOOFResults } from "../hooks/useOOFResults";
import { useSHAPValues } from "../hooks/useSHAPValues";
import "./PredictionsPanel.css";

// ----------------------------------------------------------------------------------
// configuration - trade counts, test years, and models

// set the options for trade counts, test years, and models
const TRADE_COUNTS = [1, 2, 3, 5, 10];
const FALLBACK_TOP_N = 7;
const TEST_YEARS = ["2025"];
const OOF_YEARS = ["2022", "2023", "2024"];
const MODEL_DEFAULT_LONG = 7;
const MODEL_DEFAULT_SHORT = 7;

// define the models with their ids, labels, and descriptions
const MODELS = [
  { id: "logreg", label: "Logistic Regression", desc: "Linear baseline classifier" },
  { id: "xgb", label: "XGBoost", desc: "Gradient boosted trees" },
  { id: "mlp", label: "Multi-layer Perceptron", desc: "Neural network" },
  { id: "tabnet", label: "TabNet", desc: "Deep tabular model" },
  { id: "fttransformer", label: "FT-Transformer", desc: "Deep tabular model" },
  { id: "hybrid", label: "Hybrid Ensemble", desc: "Hybrid of TabNet and XGBoost" },
];

// ----------------------------------------------------------------------------------

// function to format values based on their type (percentage, ratio, integer, or default)
const fmt = (value, type) => {
  if (value === null || value === undefined) return "—"; // if value is null or undefined, return an em dash
  if (type === "pct") return `${(value * 100).toFixed(2)}%`; // if type is percentage, multiple by 100 and round to 2dp
  if (type === "ratio") return value.toFixed(3); // if type is ratio, round to 3 decimal places
  if (type === "int") return value.toLocaleString(); // if type is integer, format with commas
  return String(value);
};

const isNeg = (value) => typeof value === "number" && value < 0; // function to check if a value is negative

// function to format capital values - 
const fmtCapital = (value) => {
  if (value == null) return "—";
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`; // if in millions, divide by 1 million and add M suffix
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}K`; // if in thousands, divide by 1 thousand and add K suffix
  
  return `$${value.toFixed(2)}`;
};

// custom tooltip component for the charts - displays the date and values for each line when hovering over a point
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null; // if any of these are not met retun null to not render the tooltip
  
  return (
    <div
      style={{
        background: "#0d1522",
        border: "1px solid rgba(232,226,214,0.12)",
        borderRadius: 4,
        padding: "8px 12px",
        fontSize: 11,
      }}
    >
      <div style={{ color: "#7d8fa3", marginBottom: 4 }}>{label}</div>
      {payload.map((p) => ( // loop over each time in the payload and display the name and value with the corresponding color
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {fmtCapital(p.value)}
        </div>
      ))}
    </div>
  );
};

// ----------------------------------------------------------------------------------

// main component for the predictions panel
const PredictionsPanel = () => {
  // set the defaults for the componennt and the states

  // set the models defaults from the hook; sets up trade limits
  const { defaultTopN: cvDefaults } = useAvailableModels();

  // trade limit states and whether we're using the model defaults or custom counts
  const [dailyLongTrades, setDailyLongTrades] = useState(FALLBACK_TOP_N);
  const [dailyShortTrades, setDailyShortTrades] = useState(FALLBACK_TOP_N);
  const [useModelDefault, setUseModelDefault] = useState(true);

  // UI state for toggling sections and modes states
  const [tradeLimitsOpen, setTradeLimitsOpen] = useState(false);
  const [longCustomMode, setLongCustomMode] = useState(false);
  const [shortCustomMode, setShortCustomMode] = useState(false);

  // model states
  const [modelOpen, setModelOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);

  // selector for the year to test on - defaults to the first year in the TEST_YEARS array
  const [selectedTestYear, setSelectedTestYear] = useState(TEST_YEARS[0]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // backtest hook to run the backtest and get the results, loading state, and any errors
  const { result: testResult, loading: testLoading, error: testError, runBacktest, reset: resetBacktest } = useBacktest();

  // hook to load OOF results for the selected model and year, with loading and error states
  const { result: oofResult, loading: oofLoading, error: oofError, loadOOF, reset: resetOOF } = useOOFResults();
  const [oofOpen, setOofOpen] = useState(false);
  const [oofYear, setOofYear] = useState("2022");

  // holdings hook to load the holdings for a given date and chart context, with loading and error states
  const { holdings, selectedDate, chartKey, loading: holdingsLoading, error: holdingsError, fetchHoldings, clearHoldings } = useHoldings();

  // SHAP values hook to load the SHAP values for the selected model, with loading and error states
  const { shapData, loading: shapLoading, error: shapError, loadSHAP, reset: resetSHAP } = useSHAPValues();
  const [shapOpen, setShapOpen] = useState(false);
  const [shapModel, setShapModel] = useState(MODELS[0]);
  const [shapTab, setShapTab] = useState("buy");

  // initialise trade limits once model defaults arrive from the hook
  useEffect(() => { // runs after the component mounts and whenever cvDefaults changes
    const init = cvDefaults[MODELS[0].id] ?? FALLBACK_TOP_N;  // get the default top_n for the first model or use the fallback value if not available
    setDailyLongTrades(init); // set the state for the daily long trades to the model default or fallback
    setDailyShortTrades(init); // set the state for the daily short trades to the model default or fallback
  }, [cvDefaults]);

  // function to handle running the backtest with the selected model, year, trade limits, and date range
  const handleRunPredictions = () => {
    runBacktest({
      model: selectedModel.id,
      timeframe: selectedTestYear,
      k_long: dailyLongTrades,
      k_short: dailyShortTrades,
      date_from: dateFrom || null,
      date_to: dateTo || null,
    });
  };

  // load OOF results for a given year
  const loadOofYear = (year) => loadOOF({ model: selectedModel.id, year });

  // function to handle the opening of the OOF section
  const handleOofOpen = () => {
    const opening = !oofOpen; // toggle the OOF section open or closed
    setOofOpen(opening); // set the state to open or close the OOF section

    if (opening) setShapOpen(false); // if we're opening the OOF section, make sure to close the SHAP section
    if (opening && !oofResult) loadOofYear(oofYear); // load the oof results if we need them and the section is open
  };

  // function to open the SHAP section
  const handleShapOpen = () => {
    const opening = !shapOpen; // toggle the SHAP section open or closed
    setShapOpen(opening); // set the state to open or close the SHAP section
    if (opening) setOofOpen(false); // if we're opening the SHAP section, make sure to close the OOF section
  };

  // function to change the selected year for OOF results and load the new year's results
  const handleOofYearChange = (yr) => {
    setOofYear(yr); // update the state with the new selected year
    loadOofYear(yr); // load the OOF results for the newly selected year
  };

  // function to get the model default top_n value or fallback if not available 
  const getModelTopN = (modelId) => {
    return cvDefaults[modelId] ?? FALLBACK_TOP_N;
  };

  // function to handle setting trade limits to the model defaults
  const handleModelDefault = () => {
    const n = getModelTopN(selectedModel.id); // run the funciton to get the model default top_n for the currently selected model

    setDailyLongTrades(n); // set both long and short trade limits to the model default
    setDailyShortTrades(n); 
    setUseModelDefault(true); // set the state to indicate that we're using the model defaults
  };

  // functions to handle setting custom trade counts and disabling model default mode if counts differ from defaults
  const handleLongTradeCount = (count) => {
    const def = getModelTopN(selectedModel.id);

    setLongCustomMode(false);
    setDailyLongTrades(count);
    setUseModelDefault(count === def && dailyShortTrades === def); // if selected count selected matches just use default
  };

  // similar function for short trade count
  const handleShortTradeCount = (count) => {
    const def = getModelTopN(selectedModel.id);

    setShortCustomMode(false);
    setDailyShortTrades(count);
    setUseModelDefault(dailyLongTrades === def && count === def);
  };

  // function to render the performance metrics section, comparing to benchmark if available
  const renderMetrics = (metrics, label, benchmarkMetrics) => {
    if (!metrics) return null;

    // array to store all the metrics we want to display, with their formatted values and raw values for coloring
    const items = [
      {
        period: "Sharpe Ratio",
        value: fmt(metrics.sharpe, "ratio"),
        raw: metrics.sharpe,
      },
      {
        period: "Sortino Ratio",
        value: fmt(metrics.sortino, "ratio"),
        raw: metrics.sortino,
      },
      {
        period: "Calmar Ratio",
        value: fmt(metrics.calmar, "ratio"),
        raw: metrics.calmar,
      },
      {
        period: "Total Return",
        value: fmt(metrics.total_return, "pct"),
        raw: metrics.total_return,
      },
      {
        period: "Max Drawdown",
        value: fmt(metrics.max_drawdown, "pct"),
        raw: metrics.max_drawdown,
      },
      {
        period: "Total Trades",
        value: fmt(metrics.n_trades, "int"),
        raw: null,
      },
    ];

    // if the benchmarks are available create a similar array for the benchmark metrics to display in a separate section below
    const benchItems = benchmarkMetrics
      ? [
          {
            period: "Sharpe Ratio",
            value: fmt(benchmarkMetrics.sharpe, "ratio"),
            raw: benchmarkMetrics.sharpe,
          },
          {
            period: "Sortino Ratio",
            value: fmt(benchmarkMetrics.sortino, "ratio"),
            raw: benchmarkMetrics.sortino,
          },
          {
            period: "Calmar Ratio",
            value: fmt(benchmarkMetrics.calmar, "ratio"),
            raw: benchmarkMetrics.calmar,
          },
          {
            period: "Total Return",
            value: fmt(benchmarkMetrics.total_return, "pct"),
            raw: benchmarkMetrics.total_return,
          },
          {
            period: "Max Drawdown",
            value: fmt(benchmarkMetrics.max_drawdown, "pct"),
            raw: benchmarkMetrics.max_drawdown,
          },
        ]
      : [];

    // return the actual JSX to render the metrics
    return (
      <div>
        <div className="section-label">{label}</div> {/* display the label for the metrics section */}
        <div className="predictions-list">
          {items.map((item, idx) => ( // loop through all of the metric items and display them
            <div key={idx} className="prediction-item">
              <span className="period">{item.period}</span> {/* display the name of the metric */}
              <span
                className={`predicted-price ${item.raw !== null ? (isNeg(item.raw) ? "negative" : "positive") : "neutral"}`}
              > {/* color the value based on whether it's positive, negative, or neutral (if we have a raw value to check) */}
                {item.value} {/* Display the value of the metric */}
              </span>
            </div>
          ))}
        </div> 

        {benchItems.length > 0 && ( // if we have metrics for the benchmark, display them in another section
          <>
            <div className="section-label" style={{ marginTop: 8 }}>
              SPY Benchmark
            </div>
            <div className="predictions-list">
              {benchItems.map((item, i) => ( // loop through all of the benchmark metric items and display them
                <div key={i} className="prediction-item benchmark-item">
                  <span className="period">{item.period}</span>
                  <span
                    className={`predicted-price ${item.raw !== null ? (isNeg(item.raw) ? "negative" : "positive") : "neutral"}`}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // function to create and show the equity curve charts for the backtesting
  const renderEquityCurve = (equityCurve, title, chartContext = { split: "test", year: null }) => {

    if (!equityCurve || equityCurve.length === 0) return null; // if we have no data to show, return null to not render the chart

    // if we have more than 252 points sample the data to avoid performance issues, otherwise show all points
    const step = Math.max(1, Math.floor(equityCurve.length / 252)); 

    // keep every nth point based on the step size, and always keep the last point to show the end of the curve
    const sampled = equityCurve.filter(
      (_, idx) => idx % step === 0 || idx === equityCurve.length - 1, 
    );

    // create a unique key for this chart based on its context to determine if it's the active chart for showing holdings
    // makes sure there isn't a collision if we have the test and oof charts open at the same time
    const thisChartKey = chartContext.split === "oof" 
      ? `oof-${chartContext.year}-k${dailyLongTrades}-${dailyShortTrades}` // if the chart is for oof do this
      : `test-k${dailyLongTrades}-${dailyShortTrades}`; // if the chart is for the test set do this

    const isActiveChart = chartKey === thisChartKey; // check if this is the active chart and store, used in the reference line to only show it on the active chart

    // function to handle the clicks on the chart and load the holdings for the selected date and chart context
    const handleChartClick = (data) => {
      
      if (!data?.activePayload?.[0]) return; // if we don't have the data we need to determine the date that was clicked on, return early and do nothing
      const date = data.activePayload[0].payload?.date; // get the date from the payload of the clicked point
      if (!date) return; // if we don't have a date, return early and do nothing

      // fetch the holdings for the selected date and chart
      fetchHoldings({
        model: selectedModel.id,
        date,
        split: chartContext.split,
        year: chartContext.year,
        kLong: dailyLongTrades,
        kShort: dailyShortTrades,
      });
    };

    // return the chart JSX
    return (
      <div className="equity-chart-section">

        <div className="equity-chart-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {title} {/* display the title for the chart */}
          <span style={{ fontSize: 10, color: "#7d8fa3", fontWeight: "normal" }}>
            — click a point to view holdings
          </span> {/* hint for the user to click on the chart to view holdings for a given date */}
        </div>

        {/* section to create the container for the chart and make the cursor a crosshair */}
        <div className="equity-chart-wrapper" style={{ cursor: "crosshair" }}>

          <ResponsiveContainer width="100%" height="100%"> {/* section to create the container for the chart */}
            <LineChart // start the line chart component, passing in the sampled data and the clicker
              data={sampled} 
              margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              onClick={handleChartClick} // add the click handler to the chart to load holdings when a point is clicked
            >

              <XAxis // configure the x-axis to show the date, with custom styling and formatting
                dataKey="date"
                tick={{ fontSize: 10, fill: "#7d8fa3" }}
                tickLine={false}
                axisLine={{ stroke: "rgba(232,226,214,0.1)" }}
                interval="preserveStartEnd"
                tickFormatter={(value) => (value ? value.slice(0, 7) : "")}
              />

              <YAxis // configure the y-axis to show the capital values, with custom styling and formatting
                tick={{ fontSize: 10, fill: "#7d8fa3" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={fmtCapital}
                width={68}
                domain={[
                  (dataMin) => Math.floor(dataMin * 0.99), // set the minimum of the y-axis to be slightly below the minimum value in the data to give some padding
                  (dataMax) => Math.ceil(dataMax * 1.01), // set the maximum of the y-axis to be slightly above the maximum value in the data to give some padding
                ]}
              />

              <Tooltip content={<CustomTooltip />} /> {/* add the custom tooltip to show values when hovering over points */}

              <Legend // add a legend to differentiate between the strategy and benchmark lines, with custom styling
                wrapperStyle={{ fontSize: 10, color: "#7d8fa3", paddingTop: 4 }}
              />

              {isActiveChart && selectedDate && ( // if this is the active chart which is open
                <ReferenceLine // plot the line to show that this is the selected data
                  x={selectedDate}
                  stroke="#b8922a"
                  strokeDasharray="3 3"
                  strokeWidth={1.5}
                />
              )}

              <Line // plot the line for the strategy value with custom styling
                type="monotone"
                dataKey="strategy_value"
                stroke="#b8922a"
                dot={false}
                strokeWidth={1.5}
                name="Portfolio"
              />

              <Line // plot the line for the benchmark value with custom styling, only if we have benchmark data available
                type="monotone"
                dataKey="benchmark_value"
                stroke="#7d8fa3"
                dot={false}
                strokeWidth={1}
                strokeDasharray="4 3"
                name="Benchmark (SPY)"
              />

            </LineChart>
          </ResponsiveContainer>
        </div>

      </div>
    );
  };

  // function to render the holdings panel for a given chart context, showing the long and short positions for the selected date, with their returns if available
  const renderHoldingsPanel = (split, year = null) => {

    // create the key for this chart to determine if it's the active chart for showing holdings
    // same reason as in the last function - make sure there isn't a collision if we have the test and oof charts open at the same time
    const thisChartKey = split === "oof" 
      ? `oof-${year}-k${dailyLongTrades}-${dailyShortTrades}`
      : `test-k${dailyLongTrades}-${dailyShortTrades}`;

    if (chartKey !== thisChartKey) return null; // if this is not the active chart for showing holdings, return null to not render the panel
    if (!holdingsLoading && !holdings && !holdingsError) return null; // if we're not loading holdings and we don't have holdings or an error, return null to not render anything
    
    // return the holdings panel JSX
    return (

      <div style={{
        marginTop: 8,
        background: "rgba(13,21,34,0.8)",
        border: "1px solid rgba(232,226,214,0.12)",
        borderRadius: 4,
        padding: "10px 12px",
      }}> {/* container for the holdings panel with custom styling */}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#e8e2d6", fontWeight: 700 }}>
            Holdings — {selectedDate} {/* Show the title of this holdings component */}
          </span>
          <button // add the button to clear the holdings and close the panel, with custom styling
            onClick={clearHoldings}
            style={{ background: "none", border: "none", color: "#7d8fa3", cursor: "pointer", fontSize: 12, padding: "0 2px" }}
            title="Close"
          >
            x
          </button>
        </div>

        {/* sections to actually show the holding info */}
        {holdingsLoading ? ( // if we're currently loading the holdings, show a loading message
          <div style={{ fontSize: 12, color: "#7d8fa3" }}>Loading holdings…</div>

        ) : holdingsError ? ( // if we have an error loading the holdings, show an error message
          <div style={{ fontSize: 12, color: "#fc8181" }}>Error: {holdingsError}</div>
          
        ) : ( // if we have no errors and we're not loading, show the holdings information
          <> {/* fragment to contain the holdings information - joins it with the current section rendered above */}

            {holdings?.total_return != null && ( // check if we have a total return value to show and display the overall return for the day with coloring based on whether it's positive or negative
              <div style={{ fontSize: 12, color: holdings.total_return >= 0 ? "#b8922a" : "#fc8181", marginBottom: 6, fontWeight: 700 }}>
                Daily L/S return (post-cost): {holdings.total_return >= 0 ? "+" : ""}{(holdings.total_return * 100).toFixed(2)}%
              </div>
            )}
            
            {holdings?.gross_return != null && holdings?.trading_cost != null && ( // if we have the gross return and trading cost values, show the gross return and costs with coloring based on whether the gross return is positive or negative
              <div style={{ fontSize: 12, color: "#7d8fa3", marginBottom: 8 }}>
                Gross: {(holdings.gross_return * 100).toFixed(2)}% | Costs: {(holdings.trading_cost * 100).toFixed(3)}%
              </div>
            )}

            {/* Display the long holdings for this day */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>

                {/* render the header for the long holdings section, showing the number of long positions */}
                <div style={{ fontSize: 12, color: "#b8922a", marginBottom: 4, fontWeight: 700 }}>
                  LONG ({holdings?.long?.length ?? 0}) 
                </div>

                {holdings?.long?.length > 0 ? ( // if we have longs to show, display them in a column
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>

                    {holdings.long.map((h) => ( // loop through all of the holdings in the array and display them
                      <div key={h.ticker} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{
                          fontSize: 12, color: "#e8e2d6",
                          background: "rgba(184,146,42,0.12)",
                          border: "1px solid rgba(184,146,42,0.25)",
                          borderRadius: 3, padding: "1px 5px",
                          minWidth: 54,
                        }}>{h.ticker}</span>

                        {h.return != null && ( // if there is a return value add it after the ticker
                          <span style={{ fontSize: 12, color: h.return >= 0 ? "#68d391" : "#fc8181" }}>
                            {h.return >= 0 ? "+" : ""}{(h.return * 100).toFixed(2)}% {/* show the return for this holding with coloring based on whether it's positive or negative */}
                          </span>
                        )}

                      </div>
                    ))}
                  </div>

                ) : ( // if we don't have any long holdings to show, display a message indicating that there are none
                  <span style={{ fontSize: 12, color: "#7d8fa3" }}>None</span>
                )}
              </div>

              {/* Display the short holdings for this day */}
              <div>
                <div style={{ fontSize: 12, color: "#fc8181", marginBottom: 4, fontWeight: 700 }}>
                  SHORT ({holdings?.short?.length ?? 0})
                </div>

                {holdings?.short?.length > 0 ? ( // check if we have holdings to show
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>

                    {holdings.short.map((h) => ( // loop through all of the holdings in the array and display them
                      <div key={h.ticker} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{
                          fontSize: 12, color: "#e8e2d6",
                          background: "rgba(239,100,100,0.1)",
                          border: "1px solid rgba(239,100,100,0.2)",
                          borderRadius: 3, padding: "1px 5px",
                          minWidth: 54,
                        }}>{h.ticker}</span>

                        {h.return != null && ( // if there is a return value add it after the ticker
                          <span style={{ fontSize: 12, color: h.return <= 0 ? "#68d391" : "#fc8181" }}>
                            {h.return >= 0 ? "+" : ""}{(h.return * 100).toFixed(2)}%
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                ) : ( // if no holdings to show, display a message indicating that there are none
                  <span style={{ fontSize: 12, color: "#7d8fa3" }}>None</span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };


  // final return statement to render the entire predictions panel
  return (
    <div className="predictions-panel">

      <div className="predictions-header">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3>Backtesting Environment</h3> {/* Header used to display the title of this panel*/}
          <span className="predictions-source"> {/* Add a source to the predictions to show the models */}
            Models: LR | XGB | MLP | TabNet | FT-Transformer | Hybrid |
          </span>
        </div>
      </div>

      {/* Model selection drop down - allow the user to select the model that will be used in the backtest*/}
      <div className="universe-section">
        <button // create a button to toggle the model selection dropdown open and closed, with custom styling
          className="universe-toggle"
          onClick={() => setModelOpen((o) => !o)}
        >
          {/* label for the model selection dropdown */}
          <span className="universe-toggle-label">
            Model
          </span>

          {/* show the currently selected model and a chevron that rotates when the dropdown is open */}
          <span className="universe-toggle-right">
            <span className="universe-badge model-badge">
              {selectedModel.label}
            </span>
            <span className={`universe-chevron ${modelOpen ? "open" : ""}`}>
              &#x25BE;
            </span>
          </span>
        </button>

        {/* actual section where the user can select the model */}
        {modelOpen && ( // if the model dropdown is open, show the options for selecting the model
          <div className="universe-body">
            <div className="model-grid">
              {MODELS.map((model) => ( // loop through the models array and create a button for each model
                <button
                  key={model.id}
                  className={`model-option-btn ${selectedModel.id === model.id ? "active" : ""}`}
                  onClick={() => {
                    // on click get all the information for the model and set the states
                    const n = getModelTopN(model.id); 
                    setSelectedModel(model);
                    setDailyLongTrades(n);
                    setDailyShortTrades(n);
                    setUseModelDefault(true);
                    setModelOpen(false);
                    resetBacktest();
                    resetOOF();
                    clearHoldings();
                  }}
                > 
                  {/* display the model label and description in the button */}
                  <span className="model-option-label">{model.label}</span>
                  <span className="model-option-desc">{model.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Trade limit selection drop down - allow the user to select the limits that will be used in the backtest*/}
      <div className="universe-section">
        <button
          className="universe-toggle"
          onClick={() => setTradeLimitsOpen((o) => !o)} // toggle the trade limits section open and closed, with custom styling depending on whether it's open or not
        >
          <span className="universe-toggle-label"> {/* label for the trade limits section */}
            Trade Limits
          </span>
          <span className="universe-toggle-right">
            <span className="universe-badge"> {/* show the current trade limits in the badge on the toggle button */}
              {dailyLongTrades}L / {dailyShortTrades}S
            </span>

            {/* show the chevron that rotates when the section is open */}
            <span
              className={`universe-chevron ${tradeLimitsOpen ? "open" : ""}`}
            >
              &#x25BE;
            </span>
          </span>
        </button>

        {/* actual section where the user can select the trade limits */}
        {tradeLimitsOpen && ( // if the trade limits section is open, show the options for selecting the trade limits
          <div className="universe-body">
            <div className="trade-limits-default-row">
              <button // create a button to set the limit to the models limit from param tuning in dev
                className={`model-default-btn ${useModelDefault ? "active" : ""}`}
                onClick={handleModelDefault} // call the hook on click
              >
                Model Default
              </button>
            </div>

            <div className="trade-limits-body">

              {/* section for selecting the long trade limits */}
              <div className="trade-limit-row">
                <span className="trade-limit-label long-label">↑ Long</span>
                <div className="trade-count-grid">
                  {TRADE_COUNTS.map((count) => ( // loop through the predefined long trade counts and create a button for each of them
                    <button
                      key={count}
                      className={`trade-count-btn long ${!longCustomMode && dailyLongTrades === count ? "active" : ""}`}
                      onClick={() => handleLongTradeCount(count)} // call the handler to set the long trade count on click
                    >
                      {count} {/* display the count in the button */}
                    </button>
                  ))}
                  <button
                    className={`trade-count-btn long ${longCustomMode ? "active" : ""}`}
                    onClick={() => setLongCustomMode((v) => !v)} // call the handler to toggle custom mode for long trades on click
                  >
                    N 
                  </button>
                </div>

                {longCustomMode ? ( // if the user has selected the custom mode for long trades 

                  <input // show an input to allow them to enter a number of trades
                    type="number"
                    min={1} // set the minimum value to 1 to avoid invalid trade counts
                    max={99} // set the maximum value to 99 to avoid excessively high trade counts that could cause performance issues
                    value={dailyLongTrades} // set the value of the input to the current long trade count
                    onChange={(e) => { // on change, update the long trade count state with the new value
                      const value = Math.max(1, parseInt(e.target.value) || 1); // parse the value as an integer and ensure that it's at least 1 to avoid invalid trade counts
                      setDailyLongTrades(value); // update the state with the new long trade count, ensuring that it's at least 1
                      setUseModelDefault( // if the new value matches the model default and the short trade count also matches the model default, set useModelDefault to true, otherwise set it to false
                        value === MODEL_DEFAULT_LONG &&
                          dailyShortTrades === MODEL_DEFAULT_SHORT,
                      );
                    }}
                    className="trade-count-input long"
                  />

                ) : ( // if we're not in custom mode, just show the current long trade count with the /day label
                  <span className="trade-count-value">
                    {dailyLongTrades}/day
                  </span>
                )}
              </div>

              {/* section for selecting the short trade limits */}
              <div className="trade-limit-row">
                <span className="trade-limit-label short-label">↓ Short</span>

                <div className="trade-count-grid">
                  {TRADE_COUNTS.map((count) => ( // loop through the predefined short trade counts and create a button for each of them
                    <button
                      key={count}
                      className={`trade-count-btn short ${!shortCustomMode && dailyShortTrades === count ? "active" : ""}`}
                      onClick={() => handleShortTradeCount(count)}
                    >
                      {count}
                    </button>
                  ))}

                  <button
                    className={`trade-count-btn short ${shortCustomMode ? "active" : ""}`}
                    onClick={() => setShortCustomMode((v) => !v)}
                  >
                    N
                  </button>
                </div>

                {shortCustomMode ? (
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={dailyShortTrades}
                    onChange={(e) => {
                      const value = Math.max(1, parseInt(e.target.value) || 1);
                      setDailyShortTrades(value);
                      setUseModelDefault(
                        dailyLongTrades === MODEL_DEFAULT_LONG &&
                          value === MODEL_DEFAULT_SHORT,
                      );
                    }}
                    className="trade-count-input short"
                  />
                ) : (
                  <span className="trade-count-value">
                    {dailyShortTrades}/day
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Test year and timeframe selection and backtest trigger section */}
      <div className="run-bar">
        <div className="run-bar-left">
          <span className="run-bar-label">Year:</span>

          <div className="run-year-tabs">
            {TEST_YEARS.map((yr) => ( // loop through the defined test years and create a button for each of them
              <button
                key={yr}
                className={`run-year-tab ${selectedTestYear === yr ? "active" : ""}`}
                onClick={() => {
                  setSelectedTestYear(yr); // if the button is clicked set the year
                  resetBacktest(); // reset the backtest results and states since we're changing a parameter
                }}
              >
                {yr} {/* display the year in the button */}
              </button>
            ))}
          </div>

          {/* Section to allow the user to select a custom time frame */}
          <span className="run-bar-label" style={{ marginLeft: 6 }}>
            From:
          </span>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)} // allow the user to select the start date for the backtest and update the state on change
            className="date-input"
          />

          <span className="run-bar-label">To:</span>

          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)} // allow the user to select the end date for the backtest and update the state on change
            className="date-input"
          />
          
          {/* if the user has selected a custom date range, show a button to clear the dates and reset to the default year-based backtest */}
          {(dateFrom || dateTo) && ( 
            <button
              className="date-clear-btn"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
              title="Clear dates"
            >
              x
            </button>
          )}
        </div>

        {/* Button to trigger the backtest with the currently selected parameters, with a loading state while the backtest is running */}
        <button
          className="run-predictions-btn"
          onClick={handleRunPredictions} // call the function to run the backtest when clicked
          disabled={testLoading}
        >
          {testLoading ? "Running…" : "Run Backtest"} 
        </button>
      </div>

      {/* if we have an error from running the backtest, show the error message here */}
      {testError && <div className="predictions-error">Error: {testError}</div>}

      {/* Section to show the actual results section of the backtest to the users */}
      <div className="predictions-content">

        {testLoading ? ( // if we are currently loading the backtest results, show a loading message
          <div className="predictions-loading">
            <span>Running backtest…</span>
          </div>

        ) : testResult ? ( // if we have backtest results, show the metrics and equity curve, along with the holdings for the selected date when clicking on the equity curve
          <div className="predictions-body">
            <div className="predictions-sections">

              {renderMetrics( // call the function to render the performance metrics for the backtest
                testResult.metrics,
                `${testResult.model?.toUpperCase()} — ${testResult.timeframe}`,
                testResult.benchmark_metrics,
              )}

              {renderHoldingsPanel("test")}
            </div>

            {renderEquityCurve( // call the function to render the equity curve for the backtest results
              testResult.equity_curve, // pass in the equity curve data from the backtest results
              "Equity Curve vs Benchmark",
              { split: "test", year: null }, // pass in the context for this chart to be the test set with no specific year, used for determining which holdings to show when clicking on the chart
            )}
          </div>
          
        ) : ( 
          <div className="predictions-placeholder">
            <span>Select a model and click Run Backtest</span> {/* show a message in the blank space before the user runs a backtest */}
            <p> 
              Runs {selectedModel.label} on the selected date range and displays
              portfolio metrics and equity curve.
            </p>
          </div>
        )}
      </div>


      {/*  Section to create the cross validation results section */}
      <div className="universe-section" style={{ marginTop: 8, flexShrink: 0 }}>
        <button className="universe-toggle" onClick={handleOofOpen}> {/* allow the section to open when clicked */}
          <span className="universe-toggle-label">
            Cross-Validation Results (Tuning Phase — Read Only)
          </span>
          <span className="universe-toggle-right">
            <span className="universe-badge">Folds 2022–2024</span>
            <span className={`universe-chevron ${oofOpen ? "open" : ""}`}>
              &#x25BE;
            </span>
          </span>
        </button>
      
        {/* section to show the cross validation results */}
        {oofOpen && ( // if the section is open
          <div className="universe-body.  oof-body">

            <div className="oof-year-tabs">
              {OOF_YEARS.map((yr) => ( // loop through all the defined years creating a button for the user to decide which fold they want toi see the results for
                <button
                  key={yr}
                  className={`oof-year-tab ${oofYear === yr ? "active" : ""}`}
                  onClick={() => handleOofYearChange(yr)} // change the year based on the button the user clicks and load the results for that year
                >
                  {yr} 
                </button>
              ))}

            </div>

            {/* if we have an error in the section just show an error message */}
            {oofError && <div className="predictions-error">{oofError}</div>}
            {oofLoading ? ( // if we are currently loading the results for the selected fold, show a loading message
              <div className="predictions-loading">Loading fold {oofYear}…</div>

            ) : oofResult ? (
              <> {/* if we have results for the selected fold, show the metrics, equity curve, and holdings for that fold */}
              <div className="oof-results-grid">

                <div className="oof-left-scroll">
                  {renderMetrics( // use the same function to render the performance metrics for this fold in the same style as the test metrics
                    oofResult.metrics,
                    `${oofResult.model?.toUpperCase()} — Fold ${oofResult.year}`,
                    oofResult.benchmark_metrics,
                  )}
                  {renderHoldingsPanel("oof", oofResult.year)} {/* use the same function to render the holdings panel for this fold, passing in the context to be the oof with the specific year to show the correct holdings when clicking on the equity curve */}
                </div>

                <div className="oof-chart-wrap">
                  {renderEquityCurve( // same as the test set to show the equity curve for the selected fold
                    oofResult.equity_curve,
                    `Equity Curve — ${oofResult.year}`,
                    { split: "oof", year: oofResult.year },
                  )}
                </div>

              </div>
              </>
            ) : null} {/* if we don't have results and we're not loading, just show nothing in the body of the section */}
          </div>
        )}
      </div>
      
      {/* final section to create the SHAP feature importance section */}
      <div className="universe-section" style={{ marginTop: 8, flexShrink: 0 }}>
        <button
          className="universe-toggle"
          onClick={handleShapOpen} // handle the opening of the shap section
        >
          <span className="universe-toggle-label">
            SHAP Feature Importance
          </span>
          <span className="universe-toggle-right">
            <span className="universe-badge model-badge">
              {shapModel.label}
            </span>
            <span className={`universe-chevron ${shapOpen ? "open" : ""}`}>
              &#x25BE;
            </span>
          </span>
        </button>

        
        {shapOpen && ( // if the section has been opened show all the information and controls for the shap feature importance
          <div className="universe-body">
            <div className="shap-controls">
              <div className="model-grid" style={{ marginBottom: 6 }}>
                {/* don't show the hybrid model as it doesnt have shap values and will error out if the user tries to load it */}
                {MODELS.filter((model) => m.id != 'hybrid').map((model) => ( // loop through all of the models and show a button for each of them to allow the user to select the model
                  <button
                    key={model.id}
                    className={`model-option-btn ${shapModel.id === model.id ? "active" : ""}`}
                    onClick={() => { // on click we want to reset the model and the shap results
                      setShapModel(model);
                      resetSHAP();
                    }}
                  >
                    <span className="model-option-label">{m.label}</span>
                    <span className="model-option-desc">{m.desc}</span>
                  </button>
                ))}
              </div>

              {/* create a button to allow the user to run the shap predictions */}
              <button
                className="run-predictions-btn"
                style={{ width: "100%" }}
                onClick={() => loadSHAP({ model: shapModel.id })} // on click, load the shap values for the selected model
                disabled={shapLoading}
              >
                {shapLoading ? "Computing SHAP…" : "Load SHAP Values"}
              </button>

              {/* If the shap values are loading or show the loading message */}
              {shapLoading && (
                <p className="universe-hint" style={{ marginTop: 4 }}>
                  SHAP may take a few minutes on first load.
                </p>
              )}
            </div>
            
            {/* if we have an error loading the shap values, show the error message */}
            {shapError && <div className="predictions-error">{shapError}</div>}

            {shapData && ( // if we have the shap data, i want to show the charts for the buy and sell signal feature importance
              <div className="shap-results">
                <div className="shap-tabs">
                  <button // add buttons to allow the user choose which signals they want to see on the plot, with styling to show which one is active
                    className={`oof-year-tab ${shapTab === "buy" ? "active" : ""}`}
                    onClick={() => setShapTab("buy")}
                  >
                    Buy Signals
                  </button>

                  <button
                    className={`oof-year-tab ${shapTab === "sell" ? "active" : ""}`}
                    onClick={() => setShapTab("sell")}
                  >
                    Sell Signals
                  </button>
                </div>

                {/* section to plot the shap values on interactive charts*/}
                <div className="shap-chart-scroll">
                <div
                  className="shap-chart-wrap" // create a wrapper for the charts with dynamic height based on the features being shown
                  style={{ height: `${Math.max(320, (shapData[shapTab] || []).length * 24)}px` }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart // begin the barchart with the data from largest to smallest
                      data={[...(shapData[shapTab] || [])].sort((a, b) => b.importance - a.importance)} // give the correct data depending on the tab selected
                      layout="vertical"
                      margin={{ top: 2, right: 16, left: 4, bottom: 2 }}
                    >
                      <XAxis // plot the x-axis with the importance values, with custom styling and formatting
                        type="number"
                        tick={{ fontSize: 9, fill: "#7d8fa3" }}
                        tickLine={false}
                        axisLine={{ stroke: "rgba(232,226,214,0.1)" }}
                        tickFormatter={(v) => v.toFixed(4)}
                      /> 

                      <YAxis // plot the y-axis with the feature names, with custom styling and no lines
                        type="category"
                        dataKey="feature"
                        tick={{ fontSize: 9, fill: "#e8e2d6" }}
                        tickLine={false}
                        axisLine={false}
                        width={130}
                      />

                      <Tooltip // allow the user to hover over the bars to see the exact feature name and importance value, with custom styling for the tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null; // if the tooltip is not active or we don't have any data to show, return null to not render the tooltip
                          return (
                            <div
                              style={{
                                background: "#0d1522",
                                border: "1px solid rgba(232,226,214,0.12)",
                                borderRadius: 4,
                                padding: "6px 10px",
                                fontSize: 11,
                              }}
                            >
                              <div
                                style={{ color: "#7d8fa3", marginBottom: 2 }}
                              >
                                {payload[0]?.payload?.feature}
                              </div>
                              <div
                                style={{
                                  color:
                                    shapTab === "buy" ? "#b8922a" : "#fc8181", // set the color based on whether it's a buy or sell signal
                                }}
                              >
                                Importance: {payload[0]?.value?.toFixed(6)}
                              </div>
                            </div>
                          );
                        }}
                      />

                      <Bar dataKey="importance" radius={[0, 2, 2, 0]}> {/* plot the bars with custom coloring based on whether it's a buy or sell signal, */}
                        {(shapData[shapTab] || []).map((_, idx) => ( // loop through all the features in the current tab and plot them and assign them a colour
                          <Cell
                            key={idx}
                            fill={ // set the colour of the bar based on whether its buy or sell and make it a gradient
                              shapTab === "buy"
                                ? `rgba(184,146,42,${0.4 + 0.6 * (1 - idx / 20)})`
                                : `rgba(239,100,100,${0.4 + 0.6 * (1 - idx / 20)})`
                            }
                          />
                        ))}
                      </Bar>

                    </BarChart>
                  </ResponsiveContainer>
                </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      
    </div>
  );
};

export default PredictionsPanel; // return the component to be used in other parts of the application
